import {ContentTileWithTitle} from '#/atoms/content-tile';
import {InfoTriggerTooltip} from '#/atoms/info-trigger-tooltip';
import {LoadingSpinner} from '#/atoms/loading-spinner';
import AsyncButton from '#/components/async-button/component';
import {WalletContext} from '#/contexts/wallet-provider';
import {useInvalidateQueries} from '#/hooks/query/base/use-invalidate-queries';
import {useLandTokenContracts} from '#/hooks/query/use-land-token-contract';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {usePortfolioClaimableLands} from '#/hooks/use-portfolio-claimable-lands';
import {ClassName} from '#/utils/utils';
import {FunctionComponent, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';

export const PortfolioRewardsTile: FunctionComponent<ClassName> = ({className}) => {
  const {
    accountResult: {address},
  } = useContext(WalletContext);

  const {t} = useTranslation('translation');

  // External hooks
  const {claimableLands, readyToClaimAmount, isLoading: claimableLandsIsLoading} = usePortfolioClaimableLands();
  const landTokenContracts = useLandTokenContracts(claimableLands);
  const displayPref = useCoinDisplayPreferences();
  const invalidateQueries = useInvalidateQueries();

  // Internal hooks
  const [isLoading, setIsLoading] = useState(false);

  // Functions
  const claimRewards = async () => {
    if (landTokenContracts) {
      setIsLoading(true);
      try {
        const res = await Promise.all(
          landTokenContracts.map(async contractQueryResult => contractQueryResult.claimRewards()),
        );
        if (!!res) {
          invalidateQueries.useLands();
          landTokenContracts.forEach(({land: {id}}) => {
            invalidateQueries.useLandMarketInfos(id);
            invalidateQueries.useLandAccountInfos(address, id);
          });
          invalidateQueries.useStableTokenBalance();
          invalidateQueries.useNativeTokenBalance();
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <ContentTileWithTitle
      title={
        <div className='w-full flex justify-between items-start'>
          <span>{t('pages.portfolio.sections.info.sections.rentingRewards.title')}</span>
          <span className='normal-case'>
            <InfoTriggerTooltip tooltipId={'portfolio-rentingRewards-tooltip'}>
              {t('pages.portfolio.sections.info.sections.rentingRewards.valueExplanationTooltip')}
            </InfoTriggerTooltip>
          </span>
        </div>
      }
      className={`flex flex-col ${className}`}
      contentClassName='grow flex justify-between gap-3 items-center pt-2 @xl/portfolio:pb-2 @4xl/portfolio:pb-0'
    >
      {claimableLandsIsLoading ? (
        <LoadingSpinner className={'w-5 h-5 text-deepblue fill-graybluedark'} />
      ) : (
        <span className='text-lg font-bold text-white'>
          {t('utils.stableTokenAmount', {
            currencySymbol: displayPref.symbol,
            amount: displayPref.displayAmount(readyToClaimAmount),
          })}
        </span>
      )}

      <AsyncButton
        className='h-8 @2xl/expandable-tile:text-sm'
        size='xs'
        onClick={claimRewards}
        disabled={claimableLandsIsLoading || readyToClaimAmount === 0 || isLoading}
      >
        {t('actions.claim')}
      </AsyncButton>
    </ContentTileWithTitle>
  );
};
